// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable from "@insite/mobius/DataTable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import {
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableRow,
    Typography,
} from "@insite/mobius/index";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { HasToasterContext } from "@insite/mobius/Toast/ToasterContext";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import importStoreList from "@store/Framework/Store/Components/StoreListUpload/Handlers/ImportStoreList";
import { ImportStoreListUploadItems } from "@store/Framework/Types/ApiModels";
import StoreListsEditListForm from "@store/Library/Widgets/StoreLists/StoreListsEditListForm";
import StoreListsImportListForm from "@store/Library/Widgets/StoreLists/StoreListsImportListForm";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface State {
    createListModalIsOpen: boolean;
    importListModalIsOpen: boolean;
    importListErrorModalIsOpen: boolean;
    nameError?: string;
    importListErrors?: ImportStoreListUploadItems[];
    canContinueImport?: boolean;
    uploadedItems: ImportStoreListUploadItems[];
    storeListName: string;
    storeListDescription: string;
}

const mapDispatchToProps = {
    importStoreList,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & HasToasterContext;

export interface StoreListsCreateListButtonStyles {
    wrapper?: InjectableCss;
    createListButton?: ButtonPresentationProps;
    createListModal?: ModalPresentationProps;
    importListButton?: ButtonPresentationProps;
    importListModal?: ModalPresentationProps;
    importListFormGridItem?: GridItemProps;
    importListErrorModal?: ModalPresentationProps;
    importErrorMessage?: TypographyProps;
    importErrorLinesWrapper?: InjectableCss;
    continueImportButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
}

export const storeListsCreateListButtonStyles: StoreListsCreateListButtonStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            margin-bottom: ${pxToRem(32)};
        `,
    },
    createListButton: {
        variant: "primary",
        css: css`
            margin: ${pxToRem(8)} 0;
        `,
    },
    createListModal: {
        sizeVariant: "small",
    },
    importListButton: {
        variant: "secondary",
        css: css`
            margin: ${pxToRem(8, 0, 8, 8)};
        `,
    },
    importListModal: {
        sizeVariant: "large",
    },
    importListFormGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    importListErrorModal: {
        sizeVariant: "medium",
    },
    importErrorMessage: {
        css: css`
            display: block;
            margin-bottom: ${pxToRem(10)};
        `,
    },
    importErrorLinesWrapper: {
        css: css`
            margin-bottom: ${pxToRem(20)};
            max-height: ${pxToRem(350)};
            overflow-y: auto;
        `,
    },
    continueImportButton: {
        css: css`
            margin-right: ${pxToRem(10)};
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
};

const styles = storeListsCreateListButtonStyles;

class StoreListsCreateListButton extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            createListModalIsOpen: false,
            importListModalIsOpen: false,
            importListErrorModalIsOpen: false,
            uploadedItems: [],
            storeListName: "",
            storeListDescription: "",
        };
    }

    createListButtonClickHandler = () => {
        this.setState({ createListModalIsOpen: true });
    };

    createListModalCloseHandler = () => {
        this.setState({ createListModalIsOpen: false });
    };

    importListButtonClickHandler = () => {
        this.setState({ importListModalIsOpen: true });
    };

    importListModalCloseHandler = () => {
        this.setState({ importListModalIsOpen: false });
    };

    importListSubmitHandler = () => {
        this.importListOnImportErrorCloseHandler();
        this.props.toaster.addToast({
            body: translate("Store List Imported"),
            messageType: "success",
        });
    };

    importListSubmitError = (errorMessage: string) => {
        this.setState({ importListErrorModalIsOpen: false });
        this.setState({ nameError: errorMessage });
    };

    importListOnImportError = (
        errors: ImportStoreListUploadItems[],
        canContinueImport: boolean,
        uploadedItems: ImportStoreListUploadItems[],
        storeListName: string,
        storeListDescription: string,
    ) => {
        this.setState({
            importListErrors: errors,
            canContinueImport,
            uploadedItems,
            storeListName,
            storeListDescription,
            importListErrorModalIsOpen: true,
        });
    };

    importListOnImportErrorCloseHandler = () => {
        this.setState({ importListErrorModalIsOpen: false });
        this.setState({ importListModalIsOpen: false });
    };

    importListContinueImportHandler = () => {
        const uploadedItems = this.state.uploadedItems;
        const storeListName = this.state.storeListName;
        const storeListDescription = this.state.storeListDescription;

        this.props.importStoreList({
            apiParameter: {
                uploadedItems,
                storeListName,
                storeListDescription,
            },
            onSuccess: this.importListSubmitHandler,
            onError: this.importListSubmitError,
        });
    };

    render() {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Button
                    {...styles.createListButton}
                    onClick={this.createListButtonClickHandler}
                    data-test-selector="storeLists_createListButton"
                >
                    {translate("Create a Store List")}
                </Button>
                <Button
                    {...styles.importListButton}
                    onClick={this.importListButtonClickHandler}
                    data-test-selector="storeLists_importListButton"
                >
                    {translate("Import a Store List")}
                </Button>
                <Modal
                    headline={translate("Create Store List")}
                    {...styles.createListModal}
                    isOpen={this.state.createListModalIsOpen}
                    handleClose={this.createListModalCloseHandler}
                >
                    <StoreListsEditListForm
                        onCancel={this.createListModalCloseHandler}
                        onSubmit={this.createListModalCloseHandler}
                    />
                </Modal>
                <Modal
                    headline={translate("Import a Store List")}
                    {...styles.importListModal}
                    isOpen={this.state.importListModalIsOpen}
                    handleClose={this.importListModalCloseHandler}
                >
                    <GridContainer>
                        <GridItem {...styles.importListFormGridItem}>
                            <StoreListsImportListForm
                                onCancel={this.importListModalCloseHandler}
                                onSubmit={this.importListSubmitHandler}
                                onImportError={this.importListOnImportError}
                                listNameErrorMessage={this.state.nameError}
                            />
                        </GridItem>
                        <GridItem {...styles.importListFormGridItem}>
                            <Zone zoneName="StoreListUploadInstructions" contentId={this.props.id} />
                        </GridItem>
                    </GridContainer>
                </Modal>
                <Modal
                    headline={translate("Import Store List Errors")}
                    {...styles.importListErrorModal}
                    isOpen={this.state.importListErrorModalIsOpen}
                >
                    {this.state.importListErrors && this.state.importListErrors.length > 0 && (
                        <>
                            <Typography {...styles.importErrorMessage}>
                                {siteMessage("StoreList_ImportErrorMessage")}
                            </Typography>
                            <StyledWrapper {...styles.importErrorLinesWrapper}>
                                <DataTable>
                                    <DataTableHead>
                                        <DataTableHeader>{translate("Customer Number")}</DataTableHeader>
                                        <DataTableHeader>{translate("Customer Ship To Number")}</DataTableHeader>
                                    </DataTableHead>
                                    <DataTableBody>
                                        {this.state.importListErrors.map(error => (
                                            <DataTableRow key={error.customerSequence}>
                                                <DataTableCell>{error.customerNumber}</DataTableCell>
                                                <DataTableCell>{error.customerSequence}</DataTableCell>
                                            </DataTableRow>
                                        ))}
                                    </DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                        </>
                    )}
                    {this.state.canContinueImport && (
                        <Button {...styles.continueImportButton} onClick={this.importListContinueImportHandler}>
                            {translate("Continue Import")}
                        </Button>
                    )}
                    <Button {...styles.cancelButton} onClick={this.importListOnImportErrorCloseHandler}>
                        {translate("Cancel")}
                    </Button>
                </Modal>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(StoreListsCreateListButton),
    definition: {
        group: constants.widgetGroup.storeLists,
        displayName: "Create Store List Button",
    },
};

export default widgetModule;
